import { Controller } from "stimulus"

const SESSION_KEY_MODE = 'pbs_v2_select_all_mode'
const SESSION_KEY_EXCLUDED = 'pbs_v2_excluded_ids'
const SESSION_KEY_EXCLUDED_AMOUNTS = 'pbs_v2_excluded_amounts'
const SESSION_KEY_SELECTED = 'pbs_v2_selected_ids'
const SESSION_KEY_SELECTED_AMOUNTS = 'pbs_v2_selected_amounts'

export default class extends Controller {
  static targets = [
    'selectAll', 'checkbox', 'selectedCount', 'totalAmount', 'submitButton',
    'feeAmount', 'netAmount',
    'totalAmountStat', 'feeAmountStat', 'netAmountStat',
    'banner', 'bannerCountPageAll', 'bannerCountGlobal',
    'globalSelectLink', 'globalSelectStatus',
    'excludedCountDisplay', 'excludedInfo', 'cancelGlobalLink', 'selectAllInput',
    'settingRadio'
  ]
  static values = { feeRate: Number, totalCount: Number, totalAmount: Number }

  connect() {
    this.selectAllMode = false
    this.selectedIds = new Set()
    this.selectedAmounts = new Map()
    this.excludedIds = new Set()
    this.excludedAmounts = new Map()
    this._restoreFromSession()
    this.updateUI()
    this._boundFormSubmit = this._onFormSubmit.bind(this)
    this.element.closest('form')?.addEventListener('submit', this._boundFormSubmit)
  }

  disconnect() {
    this.element.closest('form')?.removeEventListener('submit', this._boundFormSubmit)
  }

  // -- Public actions ---------------------------------------------------------

  toggleAll() {
    if (!this.hasSelectAllTarget) return

    const checked = this.selectAllTarget.checked

    if (!checked && this.selectAllMode) {
      this.cancelGlobalSelect()
      return
    }

    this.checkboxTargets.forEach(cb => {
      cb.checked = checked
      if (this.selectAllMode) {
        checked ? this._removeExclusion(cb) : this._addExclusion(cb)
      } else {
        checked ? this._addSelection(cb) : this._removeSelection(cb)
      }
    })

    this.selectAllMode ? this._saveToSession() : this._saveSelectedToSession()
    this.updateUI()
  }

  selectAll() {
    this.checkboxTargets.forEach(cb => {
      cb.checked = true
      this.selectAllMode ? this._removeExclusion(cb) : this._addSelection(cb)
    })

    this.selectAllMode ? this._saveToSession() : this._saveSelectedToSession()
    this.updateUI()
  }

  toggleOne(event) {
    const checkbox = event.target

    if (this.selectAllMode) {
      checkbox.checked ? this._removeExclusion(checkbox) : this._addExclusion(checkbox)
      this._saveToSession()
    } else {
      checkbox.checked ? this._addSelection(checkbox) : this._removeSelection(checkbox)
      this._saveSelectedToSession()
    }

    this.updateUI()
  }

  toggleRow(event) {
    if (event.type === 'keydown') event.preventDefault()
    if (event.target.closest('a, button, label, input')) return

    const checkbox = event.currentTarget.querySelector(
      'input[type="checkbox"][data-payback-select-target="checkbox"]'
    )
    if (!checkbox) return

    checkbox.checked = !checkbox.checked
    checkbox.dispatchEvent(new Event('change', { bubbles: true }))
    this._renderBanner()
  }

  activateGlobalSelect(event) {
    event?.preventDefault()
    this.selectAllMode = true
    this.excludedIds = new Set()
    this.excludedAmounts = new Map()
    this.checkboxTargets.forEach(cb => { cb.checked = true })
    this._saveToSession()
    this.updateUI()
  }

  cancelGlobalSelect(event) {
    event?.preventDefault()
    this.selectAllMode = false
    this.selectedIds = new Set()
    this.selectedAmounts = new Map()
    this.excludedIds = new Set()
    this.excludedAmounts = new Map()
    this._clearSession()
    this.checkboxTargets.forEach(cb => { cb.checked = false })
    if (this.hasSelectAllInputTarget) this.selectAllInputTarget.value = 'false'
    this.updateUI()
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  selectSetting(event) {
    const rate = parseFloat(event.target.dataset.feeRate || '0')
    this.feeRateValue = Number.isNaN(rate) ? 0 : rate
    this.updateUI()
  }

  // -- UI update --------------------------------------------------------------

  updateUI() {
    const checkedBoxes = this.checkboxTargets.filter(cb => cb.checked)
    const count = this._selectedCount()

    if (this.selectAllMode && count <= 0) {
      this.cancelGlobalSelect()
      return
    }

    const selectedAmount = this._selectedAmount(checkedBoxes)
    const feeRate = this.hasFeeRateValue ? this.feeRateValue : 0
    const fee = Math.round(selectedAmount * feeRate)
    const net = selectedAmount - fee

    if (this.hasSelectedCountTarget) this.selectedCountTarget.textContent = count
    if (this.hasTotalAmountTarget) this.totalAmountTarget.textContent = selectedAmount.toLocaleString('fr-FR')
    if (this.hasFeeAmountTarget) this.feeAmountTarget.textContent = fee.toLocaleString('fr-FR')
    if (this.hasNetAmountTarget) this.netAmountTarget.textContent = net.toLocaleString('fr-FR')

    const hasSetting = !this.hasSettingRadioTarget || this.settingRadioTargets.some(radio => radio.checked)
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = count === 0 || !hasSetting

    this._toggleHidden(this.hasTotalAmountStatTarget ? this.totalAmountStatTarget : null, count === 0)
    this._toggleHidden(this.hasFeeAmountStatTarget ? this.feeAmountStatTarget : null, count === 0 || fee === 0)
    this._toggleHidden(this.hasNetAmountStatTarget ? this.netAmountStatTarget : null, count === 0 || fee === 0)

    this.checkboxTargets.forEach(cb => {
      const row = cb.closest('tr')
      row?.classList.toggle('is-selected', cb.checked)
      row?.setAttribute('aria-checked', cb.checked.toString())
    })

    this._renderSelectAll()
    this._renderBanner()
  }

  // -- Private ----------------------------------------------------------------

  get _filterFingerprint() {
    const url = new URL(window.location.href)
    const params = new URLSearchParams()
    const filterKeys = ['account_id', 'query', 'created_from', 'created_to']

    filterKeys.forEach(key => {
      const value = url.searchParams.get(key)
      if (value) params.set(key, value)
    })

    return `${url.pathname}?${params.toString()}`
  }

  _selectedCount() {
    if (!this.selectAllMode) return this.selectedIds.size

    const total = this.hasTotalCountValue ? this.totalCountValue : this.checkboxTargets.length
    return Math.max(total - this.excludedIds.size, 0)
  }

  _selectedAmount(checkedBoxes) {
    if (!this.selectAllMode || !this.hasTotalAmountValue) {
      return Array.from(this.selectedAmounts.values()).reduce((sum, amount) => sum + amount, 0)
    }

    const excludedAmount = Array.from(this.excludedAmounts.values()).reduce((sum, amount) => sum + amount, 0)
    return Math.max(this.totalAmountValue - excludedAmount, 0)
  }

  _restoreFromSession() {
    const fp = this._filterFingerprint
    const selected = this._safeJsonParse(this._sessionGet(`${SESSION_KEY_SELECTED}:${fp}`), [])
    const selectedAmounts = this._safeJsonParse(this._sessionGet(`${SESSION_KEY_SELECTED_AMOUNTS}:${fp}`), {})

    this.selectedIds = new Set(selected.map(id => String(id)))
    this.selectedAmounts = new Map(Object.entries(selectedAmounts).map(([id, amount]) => [String(id), Number(amount) || 0]))

    if (this._sessionGet(`${SESSION_KEY_MODE}:${fp}`) !== 'true') {
      this.checkboxTargets.forEach(cb => {
        cb.checked = this.selectedIds.has(cb.value)
        if (cb.checked && !this.selectedAmounts.has(cb.value)) {
          this.selectedAmounts.set(cb.value, this._checkboxAmount(cb))
        }
      })
      return
    }

    this.selectedIds = new Set()
    this.selectedAmounts = new Map()
    const excluded = this._safeJsonParse(this._sessionGet(`${SESSION_KEY_EXCLUDED}:${fp}`), [])
    const excludedAmounts = this._safeJsonParse(this._sessionGet(`${SESSION_KEY_EXCLUDED_AMOUNTS}:${fp}`), {})

    this.selectAllMode = true
    this.excludedIds = new Set(excluded.map(id => String(id)))
    this.excludedAmounts = new Map(Object.entries(excludedAmounts).map(([id, amount]) => [String(id), Number(amount) || 0]))

    this.checkboxTargets.forEach(cb => {
      cb.checked = !this.excludedIds.has(cb.value)
      if (!cb.checked && !this.excludedAmounts.has(cb.value)) {
        this.excludedAmounts.set(cb.value, this._checkboxAmount(cb))
      }
    })
  }

  _saveToSession() {
    const fp = this._filterFingerprint
    this._clearSelectedSession()
    this._sessionSet(`${SESSION_KEY_MODE}:${fp}`, 'true')
    this._sessionSet(`${SESSION_KEY_EXCLUDED}:${fp}`, JSON.stringify([...this.excludedIds]))
    this._sessionSet(`${SESSION_KEY_EXCLUDED_AMOUNTS}:${fp}`, JSON.stringify(Object.fromEntries(this.excludedAmounts)))
  }

  _clearSession() {
    const fp = this._filterFingerprint
    this._sessionRemove(`${SESSION_KEY_MODE}:${fp}`)
    this._sessionRemove(`${SESSION_KEY_EXCLUDED}:${fp}`)
    this._sessionRemove(`${SESSION_KEY_EXCLUDED_AMOUNTS}:${fp}`)
    this._clearSelectedSession()
  }

  _renderSelectAll() {
    if (!this.hasSelectAllTarget) return

    const allChecked = this.checkboxTargets.length > 0 && this.checkboxTargets.every(cb => cb.checked)
    const anyChecked = this.checkboxTargets.some(cb => cb.checked)
    this.selectAllTarget.checked = allChecked
    this.selectAllTarget.indeterminate = anyChecked && !allChecked
  }

  _renderBanner() {
    if (!this.hasBannerTarget) return

    if (this.selectAllMode) {
      this._showGlobalBanner()
      return
    }

    const allChecked = this.checkboxTargets.length > 0 && this.checkboxTargets.every(cb => cb.checked)
    const anyChecked = this.checkboxTargets.some(cb => cb.checked)

    if (this._hasMoreThanCurrentPage() && allChecked) {
      this._showPageAllBanner()
    } else if (this._hasMoreThanCurrentPage() && anyChecked) {
      this._showPageAllBanner()
    } else {
      this._hideBanner()
    }
  }

  // _showPartialBanner() {
  //   this._toggleHidden(this.bannerTarget, false)
  //   this.bannerTarget.classList.add('is-info')
  //   this.bannerTarget.classList.remove('is-success')
  //   if (this.hasBannerCountPageAllTarget) this.bannerCountPageAllTarget.textContent = this._formatCount(this.hasTotalCountValue ? this.totalCountValue : 0)
  //   this._toggleHidden(this.hasGlobalSelectLinkTarget ? this.globalSelectLinkTarget : null, false)
  //   this._toggleHidden(this.hasGlobalSelectStatusTarget ? this.globalSelectStatusTarget : null, true)
  // }

  _showPageAllBanner() {
    this._toggleHidden(this.bannerTarget, false)
    this.bannerTarget.classList.add('is-info')
    this.bannerTarget.classList.remove('is-success')
    if (this.hasBannerCountPageAllTarget) this.bannerCountPageAllTarget.textContent = this._formatCount(this.hasTotalCountValue ? this.totalCountValue : 0)
    this._toggleHidden(this.hasGlobalSelectLinkTarget ? this.globalSelectLinkTarget : null, false)
    this._toggleHidden(this.hasGlobalSelectStatusTarget ? this.globalSelectStatusTarget : null, true)
  }

  _showGlobalBanner() {
    this._toggleHidden(this.bannerTarget, false)
    this.bannerTarget.classList.remove('is-info')
    this.bannerTarget.classList.add('is-success')
    if (this.hasBannerCountGlobalTarget) this.bannerCountGlobalTarget.textContent = this._formatCount(this._selectedCount())
    this._toggleHidden(this.hasGlobalSelectLinkTarget ? this.globalSelectLinkTarget : null, true)
    this._toggleHidden(this.hasGlobalSelectStatusTarget ? this.globalSelectStatusTarget : null, false)
    this._updateGlobalBannerCount()
  }

  _hideBanner() {
    this._toggleHidden(this.bannerTarget, true)
    this.bannerTarget.classList.add('is-info')
    this.bannerTarget.classList.remove('is-success')
    this._toggleHidden(this.hasGlobalSelectLinkTarget ? this.globalSelectLinkTarget : null, false)
    this._toggleHidden(this.hasGlobalSelectStatusTarget ? this.globalSelectStatusTarget : null, true)
  }

  _updateGlobalBannerCount() {
    const excluded = this.excludedIds.size
    if (this.hasExcludedInfoTarget) this.excludedInfoTarget.classList.toggle('is-hidden', excluded === 0)
    if (this.hasExcludedCountDisplayTarget) this.excludedCountDisplayTarget.textContent = this._formatCount(excluded)
  }

  _onFormSubmit() {
    const form = this.element.closest('form')
    if (!form) return

    form.querySelectorAll('input[data-payback-select-generated-exclusion]').forEach(input => input.remove())
    form.querySelectorAll('input[data-payback-select-generated-selection]').forEach(input => input.remove())

    if (!this.selectAllMode) {
      if (this.hasSelectAllInputTarget) this.selectAllInputTarget.value = 'false'
      this._appendStoredSelections(form)
      this._clearSelectedSession()
      return
    }

    if (this.hasSelectAllInputTarget) this.selectAllInputTarget.value = 'true'
    this.excludedIds.forEach(id => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'excluded_payment_ids[]'
      input.value = id
      input.dataset.paybackSelectGeneratedExclusion = 'true'
      form.appendChild(input)
    })
    this.checkboxTargets.forEach(cb => { cb.disabled = true })
    this._clearSession()
  }

  _appendStoredSelections(form) {
    const visibleIds = new Set(this.checkboxTargets.map(cb => cb.value))

    this.selectedIds.forEach(id => {
      if (visibleIds.has(id)) return

      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'payment_ids[]'
      input.value = id
      input.dataset.paybackSelectGeneratedSelection = 'true'
      form.appendChild(input)
    })
  }

  _addSelection(checkbox) {
    this.selectedIds.add(checkbox.value)
    this.selectedAmounts.set(checkbox.value, this._checkboxAmount(checkbox))
  }

  _removeSelection(checkbox) {
    this.selectedIds.delete(checkbox.value)
    this.selectedAmounts.delete(checkbox.value)
  }

  _addExclusion(checkbox) {
    this.excludedIds.add(checkbox.value)
    this.excludedAmounts.set(checkbox.value, this._checkboxAmount(checkbox))
  }

  _removeExclusion(checkbox) {
    this.excludedIds.delete(checkbox.value)
    this.excludedAmounts.delete(checkbox.value)
  }

  _checkboxAmount(checkbox) {
    return parseInt(checkbox.dataset.amount || 0, 10) || 0
  }

  _toggleHidden(target, hidden) {
    if (!target) return
    target.classList.toggle('is-hidden', hidden)
  }

  _formatCount(value) {
    return Number(value || 0).toLocaleString('fr-FR')
  }

  _hasMoreThanCurrentPage() {
    return this.hasTotalCountValue && this.totalCountValue > this.checkboxTargets.length
  }

  _saveSelectedToSession() {
    if (this.selectedIds.size === 0) {
      this._clearSelectedSession()
      return
    }

    const fp = this._filterFingerprint
    this._sessionSet(`${SESSION_KEY_SELECTED}:${fp}`, JSON.stringify([...this.selectedIds]))
    this._sessionSet(`${SESSION_KEY_SELECTED_AMOUNTS}:${fp}`, JSON.stringify(Object.fromEntries(this.selectedAmounts)))
  }

  _clearSelectedSession() {
    const fp = this._filterFingerprint
    this._sessionRemove(`${SESSION_KEY_SELECTED}:${fp}`)
    this._sessionRemove(`${SESSION_KEY_SELECTED_AMOUNTS}:${fp}`)
  }

  _safeJsonParse(value, fallback) {
    if (!value) return fallback

    try {
      return JSON.parse(value)
    } catch (_error) {
      return fallback
    }
  }

  _sessionGet(key) {
    try {
      return sessionStorage.getItem(key)
    } catch (_error) {
      return null
    }
  }

  _sessionSet(key, value) {
    try {
      sessionStorage.setItem(key, value)
    } catch (_error) {
      // Ignore storage failures; the current page remains functional.
    }
  }

  _sessionRemove(key) {
    try {
      sessionStorage.removeItem(key)
    } catch (_error) {
      // Ignore storage failures; the current page remains functional.
    }
  }
}
